import React, { useEffect, useState } from 'react';
import {
  Button,
  Label,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Alert,
  Input,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuContent,
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
  Switch,
} from '@cargomatic/ui';
import { useTranslation } from 'react-i18next';
import { showToast } from 'stores/ToastStore';
import { overrideBillingStatus } from 'utils/ShipmentWebAPIUtils';
import { PENDING_REVIEW, READY_TO_BILL } from 'constants/billing-status';
import { Check, Info, PlusCircleIcon, TriangleAlert } from 'lucide-react';
import { capitalize, uniq } from 'lodash';
import { overrideBillingStatusReasonCodes } from 'constants/override-billing-status';
import SessionStore from 'stores/SessionStore';

export function BillingStatusPendingReviewModal({
  shipmentIds = [],
  reasonCodes = [],
  note: incomingNote = '',
  open = false,
  onHide,
  onSuccess,
  showStatusToggle = false,
}) {
  const { t } = useTranslation();
  const [readyToBill, setReadyToBill] = useState(false);
  const [selectedReasonCodes, setSelectedReasonCodes] = useState(
    reasonCodes || [],
  );
  const [note, setNote] = useState(incomingNote);

  const [errors, setErrors] = useState({});
  const canChangeStatus =
    SessionStore.isBillingStatusAdmin() ||
    SessionStore.isBillingStatusManager();

  useEffect(() => {
    if (selectedReasonCodes.length > 0) {
      setReadyToBill(false);
    }
  }, [selectedReasonCodes]);

  function validate() {
    const errors = {};

    if (!note) {
      errors.note = `${t('Note')} ${t('REQUIRED')}`;
    }
    setErrors(errors);
    return errors;
  }

  function handleRemoveReasonCode(code) {
    return () => {
      setSelectedReasonCodes((items) => items.filter((item) => item !== code));
    };
  }

  function handleToggleReasonCode(code) {
    return (checked) => {
      if (checked) {
        setSelectedReasonCodes((prev) => uniq(prev.concat(code)));
      } else {
        setSelectedReasonCodes((prev) =>
          prev.filter((current) => current !== code),
        );
      }
    };
  }

  async function handleSave() {
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      const payload = shipmentIds.map((shipmentId) => ({
        _id: shipmentId,
        billing_status: readyToBill ? READY_TO_BILL : PENDING_REVIEW,
        pause_automated_billing_evaluation: readyToBill ? false : true,
        pause_automated_billing_evaluation_reason_code: note,
        pause_automated_billing_evaluation_reason_codes: selectedReasonCodes,
      }));
      await overrideBillingStatus(payload);
      showToast(`${t('BILLING_STATUS')} ${t('UPDATED')}`);
      if (onSuccess) {
        const results = payload.map((item) => ({
          _id: item._id,
          billing_status_state_billing_status: item.billing_status,
          billing_status_state_pause_automated_billing_evaluation_reason_code:
            item.pause_automated_billing_evaluation_reason_code,
          billing_status_state_pause_automated_billing_evaluation_reason_codes:
            item.pause_automated_billing_evaluation_reason_codes,
        }));
        onSuccess(results);
      }
    } catch (e) {
      showToast(`${t('BILLING_STATUS')} ${t('ERROR')}`);
      console.error(e);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onHide} className="z-50">
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Billing Status - Pending Review</DialogTitle>
          <DialogDescription>
            Move Billing Status to Pending Review
          </DialogDescription>
        </DialogHeader>
        {shipmentIds.length > 1 && (
          <Alert className="">
            <div className="flex">
              <Info className="w-4 h-4 mr-2" />
              <span>
                Any changes here will bulk update the billing status (
                {readyToBill ? 'Ready to Bill' : 'Pending Review'}), the reason
                codes, and the note fields.
              </span>
            </div>
          </Alert>
        )}
        {Object.values(errors).length > 0 && (
          <Alert variant="destructive" className="">
            <div className="flex">
              <TriangleAlert className="w-4 h-4 mr-2" />
              <div>
                Please fill out all required fields (*)
                <ul className="my-2">
                  {Object.values(errors).map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </Alert>
        )}
        <div className="space-y-4 py-4">
          {canChangeStatus && showStatusToggle && (
            <div className="flex items-center space-x-2 mb-4">
              <Switch
                id="pause-automated-evaluations"
                checked={readyToBill}
                onCheckedChange={setReadyToBill}
                disabled={selectedReasonCodes.length > 0}
              />
              <Label
                htmlFor="pause-automated-evaluations"
                className="flex items-center space-x-1"
              >
                <span>{capitalize(t('READY_TO_BILL'))}</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-4 h-4" />
                    </TooltipTrigger>
                    <TooltipContent style={{ zIndex: 3000 }}>
                      {t('PENDING_REVIEW_READY_TO_BILL_TOOLTIP_NOTE')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Label>
            </div>
          )}
          <div className="">
            <Label htmlFor="reason-code" className="block mb-1">
              {t('REASON_CODE')} *
            </Label>
            {selectedReasonCodes.length > 0 && (
              <ul className="mb-4 space-y-1">
                {selectedReasonCodes.map((item) => (
                  <li key={item} className="flex justify-between items-center">
                    <span>{t(item)}</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="icon"
                            size="icon"
                            className="hover:text-green-500"
                            onClick={handleRemoveReasonCode(item)}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Click to mark completed</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </li>
                ))}
              </ul>
            )}
            <DropdownMenu className="mb-4">
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-10 gap-1">
                  <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">
                    Select a reason code(s)
                  </span>
                  <PlusCircleIcon className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel> Select a reason code(s)</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {overrideBillingStatusReasonCodes.map((condition) => (
                  <DropdownMenuCheckboxItem
                    key={condition}
                    onCheckedChange={handleToggleReasonCode(condition)}
                    checked={selectedReasonCodes.includes(condition)}
                  >
                    {t(condition)}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="space-y-2">
            <Label htmlFor="note" className="">
              {t('Note')} *
            </Label>
            <Input
              value={note}
              id="note"
              name="note"
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="submit" onClick={handleSave}>
            {t('SAVE')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
